/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React, {useState} from 'react'
import {Button, Collapse, Dialog, DialogActions, FormControlLabel, Switch, TextField, Typography} from '@mui/material'
import {
  TranslatedField,
  translateText,
  Translation,
  useError,
  useLanguage,
  useParameters,
  useResolver,
  useTest
} from '@enisco/mcc-react-lib'
import {FormattedMessage} from 'react-intl'
import Autocomplete from '@mui/material/Autocomplete'
import useFavoriteGroups from '../../../../../hooks/useFavoriteGroups'
import {Favorite, Group} from '../../../../../model/favorites'
import useCurrentApp from '../../../../../hooks/useCurrentApp'
import useLoadProfile from '../../../../../hooks/useLoadProfile'
import {useNavigate} from 'react-router-dom'
import {pathToFavorite} from '../../../../../util/navigation'

interface Props {
  open: boolean
  onClose: () => void
  user?: string
}

const FavoriteDialog: React.FC<Props> = ({open, onClose, user}) => {
  const resolver = useResolver()
  const test = useTest()
  const error = useError()
  const language = useLanguage()
  const [groups] = useFavoriteGroups()
  const [app] = useCurrentApp()
  const [parameters] = useParameters()
  const [isNameDefined, setIsNameDefined] = useState(false)
  const [isGroupDefined, setIsGroupDefined] = useState(false)
  const [addToExistingGroup, setAddToExistingGroup] = useState(true)
  const [selectedGroup, setSelectedGroup] = useState<Group>()
  const [nameTranslations, setNameTranslations] = useState<Translation>()
  const [groupTranslations, setGroupTranslations] = useState<Translation>()
  const [loadProfile] = useLoadProfile()
  const favoriteGroups = user
    ? groups
    : groups.filter((group) => group.user === undefined)
  const navigate = useNavigate()

  const handleClose = () => {
    onClose()
  }

  const mockedData: Favorite = {
    id: '1234abc',
    app: {app: 'app', module: 'module', hasAccess: true},
    user: 'testUser',
    groupId: 'abc1234',
    group: {
      description: {default: 'Group'}
    },
    parameters: {},
    description: {default: 'Favorite'}
  }

  const saveData = () => {
    const saveFavorite = {
      app: {app: app?.app, module: app?.module},
      description: nameTranslations,
      group: addToExistingGroup
        ? undefined
        : {
            description: groupTranslations
          },
      groupId: addToExistingGroup ? selectedGroup?.id : undefined,
      user: user,
      parameters: parameters
    } as Favorite
    resolver('mcc-portal-api', '/internal/favorites', {
      options: {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(saveFavorite)
      },
      mockedResponse: test ? JSON.stringify(mockedData) : undefined
    })
      .then((response: {json: () => any}) => response.json())
      .then(async (data: {ID: string}) => {
        await loadProfile()
        return data
      })
      .then((data: {ID: string}) => {
        setNameTranslations(undefined)
        setGroupTranslations(undefined)
        setAddToExistingGroup(true)
        saveFavorite.id = data.ID
        setSelectedGroup(undefined)
        setIsNameDefined(false)
        setIsGroupDefined(false)
        onClose()
        navigate({pathname: pathToFavorite(saveFavorite.id)})
      })
      .catch(error)
  }

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby='my-favorite-dialog'
      open={open}
      PaperProps={{
        sx: {
          flexGrow: 1,
          paddingTop: 3,
          paddingLeft: 3,
          paddingRight: 3
        }
      }}
    >
      <Typography variant='h5'>
        {user ? (
          <FormattedMessage
            id='FavoriteDialog.AddFavorite'
            defaultMessage='Add favorite'
          />
        ) : (
          <FormattedMessage
            id='FavoriteDialog.AddGlobalFavorite'
            defaultMessage='Add global favorite'
          />
        )}
      </Typography>
      <TranslatedField
        onValidChange={(valid: boolean) => setIsNameDefined(valid)}
        required
        name={
          <FormattedMessage
            id='FavoriteDialog.FavoriteName'
            defaultMessage='Favorite name'
          />
        }
        onChange={(data: Translation) => {
          setNameTranslations(data)
        }}
        value={nameTranslations}
      />
      <FormControlLabel
        sx={{marginTop: 1}}
        control={
          <Switch
            data-testid='FavoriteDialog:add_to_existing_group'
            checked={addToExistingGroup}
            onChange={(_, checked) => {
              setAddToExistingGroup(checked)
              setIsGroupDefined(checked && selectedGroup !== undefined)
            }}
            name='group_switch'
          />
        }
        label={
          <FormattedMessage
            id='FavoriteDialog.AddToGroup'
            defaultMessage='Add to an existing group'
          />
        }
      />
      <Collapse in={addToExistingGroup} timeout='auto' unmountOnExit>
        <Autocomplete
          value={selectedGroup}
          onChange={(event, newValue) => {
            setSelectedGroup(newValue)
            setIsGroupDefined(true)
          }}
          id='select-module'
          disableClearable
          getOptionLabel={(option) =>
            translateText(language, option.description) ?? ''
          }
          options={favoriteGroups}
          renderInput={(params) => (
            <TextField
              {...params}
              margin='normal'
              InputProps={{...params.InputProps}}
            />
          )}
        />
      </Collapse>
      <Collapse in={!addToExistingGroup} timeout='auto' unmountOnExit>
        <TranslatedField
          onValidChange={(valid: boolean) => setIsGroupDefined(valid)}
          required
          name={
            <FormattedMessage
              id='FavoriteDialog.FavoriteGroup'
              defaultMessage='Group name'
            />
          }
          onChange={(data: Translation) => {
            setGroupTranslations(data)
          }}
          value={groupTranslations}
        />
      </Collapse>

      <DialogActions sx={{paddingTop: 5}}>
        <Button
          data-testid='FavoriteDialog:cancel_favorite'
          onClick={handleClose}
        >
          <FormattedMessage
            id='FavoriteDialog.Cancel'
            defaultMessage='Cancel'
          />
        </Button>
        <Button
          data-testid='FavoriteDialog:save_favorite'
          disabled={!(isNameDefined && isGroupDefined)}
          onClick={saveData}
        >
          <FormattedMessage id='FavoriteDialog.Ok' defaultMessage='Ok' />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FavoriteDialog
